import { useEffect, useId, useRef, useState } from 'react'

import type { TypeSoin } from '../../lib/api/types'

interface Proprietes {
  typesSoins: TypeSoin[]
  selection: string[]
  /** Appelé **une seule fois**, à la fermeture, si la sélection a changé. */
  onValider: (selection: string[]) => void
  disabled?: boolean
}

/**
 * Menu déroulant multi-sélection des soins.
 *
 * ============================================================================
 * Un multi-sélection maison est un piège d'accessibilité, et deux détails
 * décident de tout :
 *
 *   **Espace bascule une option sans fermer la liste.** C'est le geste
 *   central : une liste qui se referme à chaque coche oblige à la rouvrir
 *   quatre fois pour cocher quatre soins.
 *
 *   **Une seule requête à la fermeture.** Un `PATCH` par clic, sur un patient
 *   à quatre soins, c'est quatre allers-retours et une course dont la dernière
 *   réponse gagne — l'état affiché finit par ne plus correspondre à rien.
 *
 * Le reste suit le motif ARIA `combobox` + `listbox` : `aria-activedescendant`
 * plutôt qu'un focus déplacé, focus rendu au déclencheur à la fermeture,
 * annonce `aria-live` du nombre d'éléments choisis.
 * ============================================================================
 */
export function SelecteurSoins({ typesSoins, selection, onValider, disabled }: Proprietes) {
  const [ouvert, setOuvert] = useState(false)
  const [brouillon, setBrouillon] = useState<string[]>(selection)
  const [indexActif, setIndexActif] = useState(0)

  const identifiant = useId()
  const declencheur = useRef<HTMLButtonElement>(null)
  const liste = useRef<HTMLUListElement>(null)

  // La sélection extérieure fait autorité tant que la liste est fermée.
  useEffect(() => {
    if (!ouvert) setBrouillon(selection)
  }, [selection, ouvert])

  function fermerEnAppliquant() {
    setOuvert(false)
    declencheur.current?.focus()
    const change =
      brouillon.length !== selection.length || brouillon.some((id) => !selection.includes(id))
    if (change) onValider(brouillon)
  }

  function fermerSansAppliquer() {
    setBrouillon(selection)
    setOuvert(false)
    declencheur.current?.focus()
  }

  function basculer(id: string) {
    setBrouillon((precedent) =>
      precedent.includes(id) ? precedent.filter((x) => x !== id) : [...precedent, id],
    )
  }

  function surTouche(evenement: React.KeyboardEvent) {
    if (!ouvert) {
      if (evenement.key === 'Enter' || evenement.key === ' ' || evenement.key === 'ArrowDown') {
        evenement.preventDefault()
        setOuvert(true)
      }
      return
    }

    switch (evenement.key) {
      case 'ArrowDown':
        evenement.preventDefault()
        setIndexActif((i) => Math.min(typesSoins.length - 1, i + 1))
        break
      case 'ArrowUp':
        evenement.preventDefault()
        setIndexActif((i) => Math.max(0, i - 1))
        break
      case 'Home':
        evenement.preventDefault()
        setIndexActif(0)
        break
      case 'End':
        evenement.preventDefault()
        setIndexActif(typesSoins.length - 1)
        break
      case ' ':
        // Bascule SANS fermer : le point clé du multi-sélection.
        evenement.preventDefault()
        { const actif = typesSoins[indexActif]; if (actif) basculer(actif.id) }
        break
      case 'Enter':
        evenement.preventDefault()
        fermerEnAppliquant()
        break
      case 'Escape':
        evenement.preventDefault()
        fermerSansAppliquer()
        break
      case 'Tab':
        // Tab ferme en appliquant, et laisse le focus poursuivre son chemin.
        fermerEnAppliquant()
        break
    }
  }

  const libelleSelection =
    selection.length === 0
      ? 'Aucun soin'
      : selection.length === 1
        ? (typesSoins.find((t) => t.id === selection[0])?.libelle ?? '1 soin')
        : `${selection.length} soins sélectionnés`

  return (
    <div className="relative">
      <button
        ref={declencheur}
        type="button"
        role="combobox"
        aria-expanded={ouvert}
        aria-haspopup="listbox"
        aria-controls={`${identifiant}-liste`}
        aria-label="Soins de l’intervention"
        disabled={disabled}
        onClick={() => (ouvert ? fermerEnAppliquant() : setOuvert(true))}
        onKeyDown={surTouche}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm shadow-sm hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 disabled:bg-slate-100"
      >
        <span className="flex flex-wrap items-center gap-1">
          {selection.length === 0 ? (
            <span className="text-slate-500">Aucun soin</span>
          ) : (
            selection.map((id) => {
              const soin = typesSoins.find((t) => t.id === id)
              if (!soin) return null
              return (
                <span
                  key={id}
                  className="rounded px-1.5 py-0.5 text-xs font-medium text-white"
                  style={{ backgroundColor: soin.couleur }}
                >
                  {soin.code}
                </span>
              )
            })
          )}
        </span>
        <span aria-hidden="true" className="text-slate-400">
          ▾
        </span>
      </button>

      <span className="sr-only" aria-live="polite">
        {libelleSelection}
      </span>

      {ouvert ? (
        <ul
          ref={liste}
          id={`${identifiant}-liste`}
          role="listbox"
          aria-multiselectable="true"
          aria-label="Types de soins"
          aria-activedescendant={`${identifiant}-option-${indexActif}`}
          tabIndex={-1}
          onKeyDown={surTouche}
          className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
        >
          {typesSoins.map((soin, index) => {
            const choisi = brouillon.includes(soin.id)
            return (
              <li
                key={soin.id}
                id={`${identifiant}-option-${index}`}
                role="option"
                aria-selected={choisi}
                onMouseDown={(e) => {
                  // mousedown, pas click : évite que le bouton perde le focus
                  // et referme la liste avant d'avoir traité la bascule.
                  e.preventDefault()
                  setIndexActif(index)
                  basculer(soin.id)
                }}
                className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm ${
                  index === indexActif ? 'bg-sky-50' : ''
                }`}
              >
                <span
                  className={`flex h-4 w-4 items-center justify-center rounded border text-[10px] ${
                    choisi ? 'border-sky-600 bg-sky-600 text-white' : 'border-slate-300'
                  }`}
                  aria-hidden="true"
                >
                  {choisi ? '✓' : ''}
                </span>
                <span
                  className="h-3 w-3 rounded-full"
                  style={{ backgroundColor: soin.couleur }}
                  aria-hidden="true"
                />
                <span className="flex-1">{soin.libelle}</span>
                <span className="font-mono text-xs text-slate-500">{soin.code}</span>
              </li>
            )
          })}
          <li className="mt-1 border-t border-slate-100 px-3 pt-1.5 text-xs text-slate-500">
            Espace pour cocher, Entrée pour valider, Échap pour annuler.
          </li>
        </ul>
      ) : null}
    </div>
  )
}
